import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs, { type Dayjs } from 'dayjs';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Select from '@/components/@IntranetLibrary/Select';
import DiscountVoucherSnapshot, {
  buildDiscountItemDetailsHtml,
  buildDiscountVoucherIssueHtml,
} from '@/components/Approval/@components/DiscountVoucher';
import { getDiscountVoucherDraftSourceUrl } from '@/constant/approval';
import useMyBrandOptions from '@/hooks/approval/useMyBrandOptions';
import { useSnackbar } from '@/hooks/global-tools';
import type {
  DiscountVoucherDraftSource,
  DiscountVoucherOption,
} from '@/types/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import { FieldBlock, FieldHeader } from '../styles';
import type { ApprovalFormValues } from '../types';

const DATE_FORMAT = 'YYYY-MM-DD';
const CONTROL_HEIGHT = 44;

const toParam = (date: Dayjs | null) =>
  date?.isValid() ? date.format(DATE_FORMAT) : undefined;

/**
 * 할인권 발행(68) — 브랜드·기간 조회 → 할인권 선택 → 「확인」으로 표를 불러와 확정.
 * 드롭다운에서 다른 할인권을 고르면 확정값을 비움 — 표와 저장 번호가 어긋난 채 상신되지 않게
 */
const DiscountVoucherField = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { setValue, getValues, watch } = useFormContext<ApprovalFormValues>();
  const confirmed = watch('discountVoucher');
  const { options: brandOptions } = useMyBrandOptions('A');

  const [brandCode, setBrandCode] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(() =>
    dayjs().startOf('month'),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(() => dayjs());
  const [vouchers, setVouchers] = useState<DiscountVoucherOption[]>([]);
  const [selectedNumber, setSelectedNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (brandCode || !brandOptions.length) return;
    const saved = getValues('discountVoucher')?.brandCode;
    setBrandCode(
      brandOptions.find((o) => o.value === saved)?.value ??
        brandOptions[0].value,
    );
  }, [brandCode, brandOptions, getValues]);

  const selectVoucher = (voucherNumber: string) => {
    setSelectedNumber(voucherNumber);
    const current = getValues('discountVoucher');
    if (current && current.voucherNumber !== voucherNumber) {
      setValue('discountVoucher', null);
    }
  };

  const handleSearch = async () => {
    if (!brandCode || isSearching) return;
    setIsSearching(true);
    try {
      const data = await getNestedDataFetcher<DiscountVoucherDraftSource>(
        getDiscountVoucherDraftSourceUrl({
          brandCode,
          openDate: toParam(fromDate),
          dueDate: toParam(toDate),
        }),
      );
      const list = (data.display.vouchers ?? []).filter((v) => v.voucherNumber);
      setVouchers(list);
      // 확정한 할인권이 결과에 있으면 유지, 없으면 레거시처럼 첫 건. 빈 결과면 확정값은 그대로
      const confirmedNumber = getValues('discountVoucher')?.voucherNumber;
      const next =
        list.find((v) => v.voucherNumber === confirmedNumber) ?? list[0];
      if (next?.voucherNumber) selectVoucher(next.voucherNumber);
      else {
        setSelectedNumber('');
        snackbar.showError({ message: t('조회된 할인권이 없습니다.') });
      }
    } catch {
      snackbar.showError({
        message: t(
          '할인권 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
        ),
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirm = async () => {
    if (isConfirming) return;
    const option = vouchers.find((v) => v.voucherNumber === selectedNumber);
    if (!option?.voucherNumber) {
      snackbar.showError({ message: t('목록을 선택하세요.') });
      return;
    }
    setIsConfirming(true);
    try {
      const data = await getNestedDataFetcher<DiscountVoucherDraftSource>(
        getDiscountVoucherDraftSourceUrl({
          brandCode: option.brandCode || brandCode,
          openDate: toParam(fromDate),
          dueDate: toParam(toDate),
          voucherNumber: option.voucherNumber,
        }),
      );
      const { attributes, display } = data;
      if (!display.terms?.length) {
        snackbar.showError({
          message: t(
            '할인 조건을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
          ),
        });
        return;
      }
      const brandName = attributes.brandName ?? option.brandName ?? '';
      const shopCode = attributes.shopCode ?? option.shopCode ?? null;
      const shopName = attributes.shopName ?? option.shopName ?? null;
      setValue('discountVoucher', {
        brandCode: attributes.brandCode || option.brandCode || brandCode,
        brandName,
        shopCode,
        shopName,
        voucherNumber: option.voucherNumber,
        voucherIssueHtml: buildDiscountVoucherIssueHtml(
          {
            brandName,
            shopCode,
            shopName,
            terms: display.terms,
            targetSeasons: display.targetSeasons ?? [],
          },
          t,
        ),
        discountItemDetailsHtml: buildDiscountItemDetailsHtml(
          display.targetItems ?? [],
          t,
        ),
      });
    } catch {
      snackbar.showError({
        message: t(
          '할인권 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
        ),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <FieldBlock>
      <FieldHeader>
        <Body1>{t('할인권 발행')}</Body1>
      </FieldHeader>
      <ControlRow>
        <Select
          value={brandCode}
          options={brandOptions}
          placeholder={t('브랜드')}
          onChange={(e) => setBrandCode(String(e.target.value))}
          sx={{ width: 150, height: CONTROL_HEIGHT }}
        />
        <DateRange>
          <DatePicker
            value={fromDate}
            onChange={setFromDate}
            format={DATE_FORMAT}
            sx={{ width: 160 }}
          />
          <span>~</span>
          <DatePicker
            value={toDate}
            onChange={setToDate}
            format={DATE_FORMAT}
            sx={{ width: 160 }}
          />
        </DateRange>
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          disabled={!brandCode || isSearching}
          onClick={handleSearch}
        >
          {t('작성일 조회')}
        </BoxButton>
      </ControlRow>
      <ControlRow>
        <Select
          value={selectedNumber}
          options={vouchers.map((v) => ({
            value: v.voucherNumber ?? '',
            label: v.label ?? v.voucherNumber ?? '',
          }))}
          placeholder={t('할인권을 조회해 주세요.')}
          onChange={(e) => selectVoucher(String(e.target.value))}
          sx={{ flex: 1, minWidth: 0, maxWidth: 600, height: CONTROL_HEIGHT }}
        />
        <BoxButton
          type="button"
          variant="keyColor"
          size="medium"
          disabled={isConfirming}
          onClick={handleConfirm}
        >
          {t('확인')}
        </BoxButton>
      </ControlRow>
      {confirmed && (
        <DiscountVoucherSnapshot
          key={confirmed.voucherNumber}
          voucherIssueHtml={confirmed.voucherIssueHtml}
          discountItemDetailsHtml={confirmed.discountItemDetailsHtml}
        />
      )}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const DateRange = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export default DiscountVoucherField;
